import axios from 'axios'
import qs from 'qs'
import Login from './login'
import { getCommonParams, sendRaptorErr, AESEncrypt } from './util'
import toast from '../components/toast/index'
import env from './env'

// 创建一个axios实例，设置基本配置
const Ajax = axios.create({
  baseURL: process.env.VUE_APP_SERVER_URL || 'https://quickpass.meituan.com',
  timeout: 6000,
  method: 'post',
  withCredentials: true,
  headers: {
    'content-type': 'application/x-www-form-urlencoded; charset=utf-8',
  },
})

// 🟩 [新增] 创建额度接口专用的axios实例，使用 VUE_APP_QUOTA_HOST，兜底为 npay.meituan.com
const QuotaAjax = axios.create({
  baseURL: process.env.VUE_APP_QUOTA_HOST || 'https://npay.meituan.com',
  timeout: 6000,
  method: 'post',
  withCredentials: true,
  headers: {
    'content-type': 'application/x-www-form-urlencoded; charset=utf-8',
  },
})

// 🟩 【新增】MSI 实例缓存（仅在鸿蒙系统中）
let msiInstance = null
let msiLoadPromise = null

/**
 * 获取 MSI 实例
 * 参考：https://km.sankuai.com/collabpage/2712295141
 */
const getMSI = async () => {
  if (!(env.isHarmony && env.isKNB)) {
    console.log('ℹ️ [MSI] 非鸿蒙系统，不使用msi')
    return null
  }

  // 如果已经加载过，直接返回
  if (msiInstance) {
    return msiInstance
  }

  // 如果正在加载中，等待加载完成
  if (msiLoadPromise) {
    console.log('⏳ [MSI] 正在加载中，等待...')
    return msiLoadPromise
  }

  // 开始加载 MSI
  msiLoadPromise = (async () => {
    try {
      console.log('📦 [MSI] 动态导入 @mtfe/msi-knb-loader...')
      const { msi } = await import('@mtfe/msi-knb-loader')

      // 尝试获取更详细的鉴权信息
      const loadRes = await msi.load()
      
      console.log('🔍 [MSI] msi 对象详情:', {
        hasLoad: typeof msi.load === 'function',
        hasRequest: typeof msi.request === 'function',
        msiKeys: Object.keys(msi),
        msiPrototype: Object.getPrototypeOf(msi)
      })

      if (loadRes) {
        msiInstance = msi
        return msi
      } else {
        // 获取更详细的鉴权失败信息
        const errorDetail = {
          loadResult: loadRes,
          msiObject: msi,
          windowMsi: typeof window !== 'undefined' ? window.msi : undefined,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
          location: typeof window !== 'undefined' ? window.location.href : undefined
        }
        console.error('❌ [MSI] 鉴权失败详情:', errorDetail)
        sendRaptorErr('MSI鉴权失败', errorDetail, 'customError')

        // 重置 Promise，允许重试
        msiLoadPromise = null
        return null
      }
    } catch (error) {
      console.error('❌ [MSI] 导入或初始化失败:', error)
      sendRaptorErr('MSI导入或初始化失败', error, 'customError')

      // 重置 Promise，允许重试
      msiLoadPromise = null
      return null
    }
  })()

  return msiLoadPromise
}

// 日志和错误发送函数
const logAndSendError = (message, error, type) => {
  console.warn(message, error)
  sendRaptorErr(message, error, type)
}

// 处理请求错误
const handleRequestError = (err) => {
  logAndSendError('请求异常', err, 'ajaxError')
  return Promise.reject(err)
}

// 处理响应错误
const handleResponseError = (response) => {
  logAndSendError('响应异常', response, 'ajaxError')
  toast.show({ type: 'text', text: response.statusText })
  return Promise.reject(response.statusText)
}

// 处理服务器错误
const handleServerError = (response) => {
  logAndSendError('服务端响应异常', response, 'ajaxError')
  const code = response.data.error.code
  const errorHandlers = {
    20001: () => {
      Login()
      return Promise.reject({ code, message: '请重新登录' })
    },
    30001: () => {
      Login()
      return Promise.reject({ code, message: '请重新登录' })
    },
    300110: () => Promise.reject({ code }),
    300301: () => Promise.reject({ code }),
    300302: () => Promise.reject({ code }),
    300104: () => Promise.reject({ code }),
    default: () => Promise.reject(response.data.error),
  }
  return (errorHandlers[code] || errorHandlers.default)()
}

// 🟩 【新增】使用 MSI 发送请求
// 参考文档：https://msi-extend.sankuai.com/api?tab=C&id=1539
const sendRequestWithMSI = (msi, config) => {
  return new Promise((resolve, reject) => {
    try {
      // 🟩 [修改] 根据是否为额度接口选择正确的 baseURL
      const quotaRelatedUrls = [
        '/paycustomer/sign/quota/getQuotaInfo',
        '/paycustomer/sign/quota/adjustUserQuota'
      ]
      const isQuotaApi = quotaRelatedUrls.some(url => config.url.includes(url))
      
      let baseURL
      if (isQuotaApi) {
        // 🟩 [修改] 额度接口使用 npay.meituan.com 作为兜底
        baseURL = config.baseURL || QuotaAjax.defaults.baseURL || process.env.VUE_APP_QUOTA_HOST || 'https://npay.meituan.com'
        console.log('📡 [MSI] 检测到额度接口，使用 QuotaAjax baseURL:', baseURL)
      } else {
        baseURL = config.baseURL || Ajax.defaults.baseURL
      }

      // 使用 URL 构造函数拼接 URL（自动处理斜杠和完整 URL）
      const fullUrl = new URL(config.url, baseURL).href
      // 使用 window.msi 而不是导入的 msi 对象
      const msiRequest = typeof window !== 'undefined' && window.msi ? window.msi : msi
      
      const msiHeaders = {
        'content-type': 'application/x-www-form-urlencoded; charset=utf-8',
        'user-agent': navigator.userAgent || '', // 鸿蒙KNB容器在美团12.48.400以下header中未添加ua公参，需要手动添加
        ...config.headers
      }
      console.log('🔍 [MSI] 请求配置:', {
        url: fullUrl,
        method: config.method || 'POST',
        headers: msiHeaders,
        timeout: config.timeout || 6000,
        withCredentials: true
      })

      // 调用 MSI 的 request 方法
      // 基建网络请求会自动在 header 中带上 mtgsig 签名
      msiRequest.request({
        url: fullUrl,
        method: config.method || 'POST',
        header: msiHeaders,
        data: config.data,
        timeout: config.timeout || 6000,
        success: (response) => {
          console.log('📥 [MSI] 收到响应:', response.data)

          if (response.data.status !== 'success') {
            handleServerError(response)
              .catch((err) => {
                if (
                  err.message === 'Network Error' ||
                  err.message.startsWith('timeout of')
                ) {
                  logAndSendError('网络异常/请求超时', err, 'customError')
                } else {
                  logAndSendError('未知响应异常', err, 'customError')
                }
                reject({ code: 100000, message: '断网环境' })
            })
            return
          }
          
          let responseData = response.data.data
          
          // 🟩 [新增] paystatus 接口如果返回了 encrypt_res，需要解密后替换整个响应数据
          if (config.url === '/qrcode/app/merchantscan/paystatus' && responseData && responseData.encrypt_res) {
            try {
              const AESKey = localStorage.getItem('PAY_RESULT_AESKey')
              if (AESKey) {
                const decryptData = AESEncrypt('decrypt', {
                  key: AESKey,
                  data: responseData.encrypt_res,
                })
                // 将解密后的数据替换整个响应对象，去掉 encrypt_res 字段
                responseData = JSON.parse(decryptData.toString())
                console.log('🔓 [MSI PayStatus] 已解密 encrypt_res: ', responseData)
              } else {
                console.warn('⚠️ [MSI PayStatus] 缺少 PAY_RESULT_AESKey，无法解密 encrypt_res')
              }
            } catch (e) {
              console.error('❌ [MSI PayStatus] 解密 encrypt_res 失败:', e)
              sendRaptorErr('MSI PayStatus 解密失败', e, 'customError')
            }
          }
          
          resolve(responseData)
        },
        fail: (error) => {
          console.error('❌ [MSI] 请求失败:', {
            error: error,
            message: error.message || error,
            timestamp: new Date().toISOString()
          })
          reject({
            code: 100000,
            message: '网络请求失败',
            error,
          })
        }
      })
    } catch (error) {
      console.error('❌ [MSI] 请求失败:', error)
      reject(error)
    }
  })
}

// 🟩 [新增] 环境参数映射，参考 https://km.sankuai.com/collabpage/1852495096
const getEnvironmentParams = (commonParams) => {
  return {
    // 基础环境参数
    nb_app: commonParams.app || 'unknown',
    nb_platform: commonParams.platform || 'unknown',
    nb_uuid: commonParams.uuid || '',
    nb_location: '', // 可根据需要从其他来源获取
    nb_fingerprint: commonParams.fingerprint || '',
    nb_appversion: commonParams.appVersion || '',
    nb_ci: commonParams.cityId || '',
    nb_deviceid: commonParams.deviceId || '',
  }
}

// 请求拦截器
Ajax.interceptors.request.use(
  async (config) => {
    try {
      const commonParams = await getCommonParams()
      if (config.url === '/qrcode/offline/getseed') {
        localStorage.setItem('AESkey', commonParams.AESKey)
      }
      // 🟩 [新增] paystatus 接口在 codeFlag=1 时也需要保存 AESKey（使用 PAY_RESULT_AESKey 做区分）
      if (config.url === '/qrcode/app/merchantscan/paystatus') {
        const requestParams = config.data || {}
        if (requestParams.codeFlag === 1 || requestParams.codeFlag === '1') {
          localStorage.setItem('PAY_RESULT_AESKey', commonParams.AESKey)
        }
      }
      
      config.data = qs.stringify(Object.assign(commonParams, config.data))
    } catch (e) {
      if (config.url === '/qrcode/offline/getseed') {
        localStorage.setItem('AESkey', e.AESKey)
      }
      // 🟩 [新增] paystatus 接口在 codeFlag=1 时也需要保存 AESKey（异常时，使用 PAY_RESULT_AESKey 做区分）
      if (config.url === '/qrcode/app/merchantscan/paystatus') {
        const requestParams = config.data || {}
        if (requestParams.codeFlag === 1 || requestParams.codeFlag === '1') {
          localStorage.setItem('PAY_RESULT_AESKey', e.AESKey)
        }
      }
      config.data = qs.stringify(Object.assign(e, config.data))
    }
    return config
  },
  handleRequestError
)

// 🟩 [新增] QuotaAjax 请求拦截器（包含环境参数）
QuotaAjax.interceptors.request.use(
  async (config) => {
    try {
      const commonParams = await getCommonParams()
      
      // 为额度接口添加环境参数
      const environmentParams = getEnvironmentParams(commonParams)
      const mergedParams = Object.assign({}, commonParams, config.data, environmentParams)
      
      config.data = qs.stringify(mergedParams)
      console.log('📡 [QuotaAjax] 额度接口已添加环境参数:', environmentParams)
    } catch (e) {
      config.data = qs.stringify(Object.assign(e, config.data))
    }
    return config
  },
  handleRequestError
)

// 响应拦截器
Ajax.interceptors.response.use(
  (response) => {
    if (response.status !== 200) {
      return handleResponseError(response)
    }
    if (response.data.status !== 'success') {
      return handleServerError(response)
    }
    
    let responseData = response.data.data
    
    // 🟩 [新增] paystatus 接口如果返回了 encrypt_res，需要解密后替换整个响应数据
    if (response.config.url === '/qrcode/app/merchantscan/paystatus' && responseData && responseData.encrypt_res) {
      try {
        const AESKey = localStorage.getItem('PAY_RESULT_AESKey')
        if (AESKey) {
          const decryptData = AESEncrypt('decrypt', {
            key: AESKey,
            data: responseData.encrypt_res,
          })
          // 将解密后的数据替换整个响应对象，去掉 encrypt_res 字段
          responseData = JSON.parse(decryptData.toString())
          console.log('🔓 [PayStatus] 已解密 encrypt_res:', responseData)
        } else {
          console.warn('⚠️ [PayStatus] 缺少 PAY_RESULT_AESKey，无法解密 encrypt_res')
        }
      } catch (e) {
        console.error('❌ [PayStatus] 解密 encrypt_res 失败:', e)
        sendRaptorErr('PayStatus 解密失败', e, 'customError')
      }
    }
    
    return responseData
  },
  (err) => {
    if (
      err.message === 'Network Error' ||
      err.message.startsWith('timeout of')
    ) {
      logAndSendError('网络异常/请求超时', err, 'ajaxError')
    } else {
      logAndSendError('未知响应异常', err, 'ajaxError')
    }
    return Promise.reject({ code: 100000, message: '断网环境' })
  }
)

// 🟩 [新增] QuotaAjax 响应拦截器
QuotaAjax.interceptors.response.use(
  (response) => {
    if (response.status !== 200) {
      return handleResponseError(response)
    }
    if (response.data.status !== 'success') {
      return handleServerError(response)
    }
    return response.data.data
  },
  (err) => {
    if (
      err.message === 'Network Error' ||
      err.message.startsWith('timeout of')
    ) {
      logAndSendError('网络异常/请求超时', err, 'ajaxError')
    } else {
      logAndSendError('未知响应异常', err, 'ajaxError')
    }
    return Promise.reject({ code: 100000, message: '断网环境' })
  }
)

// 🟩 【修改】使用 if-else 替代 Proxy，避免浏览器兼容性问题
const AjaxWrapper = async (config = {}) => {
  console.log('🚀 [AjaxWrapper] 请求开始, URL:', config.url, 'env:', { isHarmony: env.isHarmony, isKNB: env.isKNB })
  
  // 🟩 [新增] 判断是否为额度相关接口
  const quotaRelatedUrls = [
    '/paycustomer/sign/quota/getQuotaInfo',
    '/paycustomer/sign/quota/adjustUserQuota'
  ]
  const isQuotaApi = quotaRelatedUrls.some(url => config.url.includes(url))
  console.log('🔍 [AjaxWrapper] isQuotaApi:', isQuotaApi)
  
  // 鸿蒙系统下使用 MSI
  if (env.isHarmony && env.isKNB) {
    try {
      console.log('🔍 [AjaxWrapper] 检测到鸿蒙系统，尝试使用 MSI')
      const msi = await getMSI()
      if (msi) {
        // 获取公共参数
        const commonParams = await getCommonParams()
        if (config.url === '/qrcode/offline/getseed') {
          localStorage.setItem('AESkey', commonParams.AESKey)
        }
        // 🟩 [新增] paystatus 接口在 codeFlag=1 时也需要保存 AESKey（使用 PAY_RESULT_AESKey 做区分）
        if (config.url === '/qrcode/app/merchantscan/paystatus') {
          const requestParams = config.data || {}
          if (requestParams.codeFlag === 1 || requestParams.codeFlag === '1') {
            localStorage.setItem('PAY_RESULT_AESKey', commonParams.AESKey)
          }
        }

        // 🟩 [修改] 为所有 MSI 请求合并公共参数，额度接口额外添加环境参数
        let mergedData = Object.assign({}, commonParams, config.data)
        if (isQuotaApi) {
          const environmentParams = getEnvironmentParams(commonParams)
          mergedData = Object.assign({}, mergedData, environmentParams)
          console.log('📡 [AjaxWrapper MSI] 额度接口已添加环境参数:', environmentParams)
        }

        // 使用 MSI 发送请求
        // 🟩 [修改] 根据接口类型设置正确的 baseURL，额度接口兜底为 npay.meituan.com
        let baseURLForMSI = config.baseURL || Ajax.defaults.baseURL
        if (isQuotaApi) {
          baseURLForMSI = QuotaAjax.defaults.baseURL || process.env.VUE_APP_QUOTA_HOST || 'https://npay.meituan.com'
          console.log('📡 [AjaxWrapper MSI] 检测到额度接口，使用 QuotaAjax baseURL:', baseURLForMSI)
        }
        return await sendRequestWithMSI(msi, {
          ...config,
          baseURL: baseURLForMSI,
          data: qs.stringify(mergedData),
        })
      } else {
        sendRaptorErr('MSI导入或初始化失败', {}, 'customError')
        console.error('⚠️ [AjaxWrapper] MSI 不可用，不降级到 axios，直接抛出错误')
        throw { code: 100000, message: 'MSI 不可用' }
      }
    } catch (error) {
      sendRaptorErr('MSI请求异常', error, 'customError')
      console.error('❌ [AjaxWrapper] MSI 请求失败，不降级到 axios:', error)
      // 不降级到 axios，直接抛出错误
      throw error
    }
  } else {
    console.log('ℹ️ [AjaxWrapper] 非鸿蒙系统，使用 axios')
    // 🟩 [新增] 根据接口类型选择使用哪个 Ajax 实例
    if (isQuotaApi) {
      console.log('📡 [AjaxWrapper] 检测到额度接口，使用 QuotaAjax')
      return QuotaAjax(config)
    }
    // 其他系统使用 axios
    return Ajax(config)
  }
}

export default AjaxWrapper
